import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, Stack, Button, TextField, MenuItem, Select, FormControl, InputLabel, IconButton, Grid, Checkbox, ListItemText, Tabs, Tab, Paper } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsIcon from '@mui/icons-material/Settings';
import EmailStatusChip from './EmailStatusChip';
import { getEmailLogs, exportEmailLogsCSV } from '../../Api/SystemEmailLogs';
import { STATUS_OPTIONS } from '../../mocks/emailNotifications';
import StandardListView from '../StandardComponents/StandardListView/StandardListView';
import StandardFormView from '../StandardComponents/StandardFormView/StandardFormView';
import StandardHighlight from '../StandardComponents/StandardHighlight/StandardHighlight';
import EmailNotificationsSettings from './EmailNotificationsSettings';
import QueuedEmailForecast from './QueuedEmailForecast';
import { hasSuperOrTechAdminOnly } from '../../Utils/adminUserTypes';
import { EMAIL_CONTEXTS, getContextLabel } from '../../Constants/emailContexts';
import useD8ListLayout from '../../hooks/useD8ListLayout';

const NOTIFICATIONS_TABS = { EMAIL_LOG: 'email-log', QUEUED_FORECAST: 'queued-forecast' };

const EmailNotificationsPage = () => {
  const [activeNotificationsTab, setActiveNotificationsTab] = useState(NOTIFICATIONS_TABS.EMAIL_LOG);
  const { pageShellSx, listFlexSx, listViewProps } = useD8ListLayout();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [query, setQuery] = useState("");
  const [status, setStatus] = useState([]);
  const [contextType, setContextType] = useState([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState("current"); // "current" or "all"
  const [highlightsExpanded, setHighlightsExpanded] = useState(false); // Track Key Highlights state

  const location = useLocation();
  const navigate = useNavigate();

  // Debug highlights state
  useEffect(() => {
    console.log('[EmailNotifications] Highlights expanded:', highlightsExpanded);
  }, [highlightsExpanded]);
  
  // Get organization ID from local storage (set during login)
  const organizationId = localStorage.getItem('organizationId') || '';

  // Debug: Verify organization ID
  useEffect(() => {
    console.log('[EmailNotifications] Organization ID from localStorage:', organizationId);
    console.log('[EmailNotifications] Expected:', 'f73a99eb-e018-49bb-8d0c-447b2f47bb11');
    if (!organizationId) {
      console.warn('[EmailNotifications] No organization ID found in localStorage!');
    }
  }, [organizationId]);

  const fetchData = async (params = {}) => {
    setLoading(true);
    setError("");
    try {
      // Determine organization ID based on selection
      const effectiveOrgId = (params.selectedOrg !== undefined ? params.selectedOrg : selectedOrg) === "all" 
        ? null 
        : organizationId;
      
      console.log('[EmailNotifications] API Request:', {
        organizationId: effectiveOrgId,
        selectedOrg: params.selectedOrg !== undefined ? params.selectedOrg : selectedOrg,
        page: (params.page !== undefined ? params.page : page) + 1,
        pageSize: params.pageSize !== undefined ? params.pageSize : pageSize,
        query: params.q !== undefined ? params.q : query
      });

      const apiParams = {
        q: params.q !== undefined ? params.q : query,
        status: params.status !== undefined ? params.status : status,
        contextType: params.contextType !== undefined ? params.contextType : contextType,
        from: params.from !== undefined ? params.from : from,
        to: params.to !== undefined ? params.to : to,
        page: (params.page !== undefined ? params.page : page) + 1,
        pageSize: params.pageSize !== undefined ? params.pageSize : pageSize,
        organizationId: effectiveOrgId
      };
      
      console.log('[EmailNotifications] API call params:', apiParams);
      
      const response = await getEmailLogs(apiParams);
      
      console.log('[EmailNotifications] API Response:', {
        response,
        data: response.data,
        items: response.data?.items,
        total: response.data?.total,
        itemsCount: response.data?.items?.length || 0
      });
      
      const data = response.data || {};
      setRows(data.items || []);
      setTotal(data.total || 0);
      
      console.log('[EmailNotifications] State updated:', {
        rowsCount: data.items?.length || 0,
        total: data.total || 0
      });
    } catch (e) {
      setError('Failed to load email logs');
      console.error('[EmailNotifications] Error:', e);
    } finally {
      setLoading(false);
    }
  };

  // Parse URL parameters on mount and fetch data with those parameters
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const initQ = sp.get('q') || '';
    const initStatus = sp.getAll('status');
    const initContext = sp.getAll('context');
    const initFrom = sp.get('from') || '';
    const initTo = sp.get('to') || '';
    const initPage = parseInt(sp.get('page') || '1', 10) - 1;
    const initPageSize = parseInt(sp.get('pageSize') || '25', 10);
    const initOrg = sp.get('org') || 'current';
    
    // Set state for UI
    setQuery(initQ);
    if (initStatus.length) setStatus(initStatus);
    if (initContext.length) setContextType(initContext);
    setFrom(initFrom);
    setTo(initTo);
    setSelectedOrg(initOrg);
    if (!Number.isNaN(initPage)) setPage(Math.max(0, initPage));
    if (!Number.isNaN(initPageSize)) setPageSize(Math.max(1, initPageSize));
    
    // Fetch data immediately with URL parameters (don't wait for state updates)
    fetchData({
      q: initQ,
      status: initStatus,
      contextType: initContext,
      from: initFrom,
      to: initTo,
      page: Math.max(0, initPage),
      pageSize: Math.max(1, initPageSize),
      selectedOrg: initOrg
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyUrlAndReload = (nextState = {}) => {
    const next = {
      q: nextState.q !== undefined ? nextState.q : query,
      status: nextState.status !== undefined ? nextState.status : status,
      contextType: nextState.contextType !== undefined ? nextState.contextType : contextType,
      from: nextState.from !== undefined ? nextState.from : from,
      to: nextState.to !== undefined ? nextState.to : to,
      page: nextState.page !== undefined ? nextState.page : page,
      pageSize: nextState.pageSize !== undefined ? nextState.pageSize : pageSize,
      selectedOrg: nextState.selectedOrg !== undefined ? nextState.selectedOrg : selectedOrg,
    };

    console.log('[EmailNotifications] applyUrlAndReload called:', {
      nextState,
      mergedNext: next,
      currentState: { query, status, contextType, from, to, page, pageSize, selectedOrg }
    });

    const sp = new URLSearchParams(location.search);
    // Preserve the 'view' parameter - critical for staying on Email Notifications page
    const currentView = sp.get('view');
    
    // Clear and rebuild with new parameters
    sp.delete('q');
    sp.delete('status');
    sp.delete('context');
    sp.delete('from');
    sp.delete('to');
    sp.delete('page');
    sp.delete('pageSize');
    sp.delete('org');
    
    if (next.q) sp.set('q', next.q);
    (next.status || []).forEach(s => sp.append('status', s));
    (next.contextType || []).forEach(c => sp.append('context', c));
    if (next.from) sp.set('from', next.from);
    if (next.to) sp.set('to', next.to);
    sp.set('page', String((next.page || 0) + 1));
    sp.set('pageSize', String(next.pageSize));
    if (next.selectedOrg) sp.set('org', next.selectedOrg);
    // Ensure view parameter is preserved
    if (currentView) sp.set('view', currentView);
    
    const urlString = sp.toString();
    console.log('[EmailNotifications] URL params:', urlString);
    navigate({ pathname: location.pathname, search: urlString }, { replace: true });
    
    // Fetch with the new parameters immediately
    console.log('[EmailNotifications] Calling fetchData with:', next);
    fetchData(next);
  };

  const handleSearch = (value) => {
    setQuery(value);
    setPage(0);
    applyUrlAndReload({ q: value, page: 0 });
  };

  const handleClearSearch = () => {
    setQuery("");
    setPage(0);
    applyUrlAndReload({ q: "", page: 0 });
  };

  const onExportCsv = async () => {
    try {
      const effectiveOrgId = selectedOrg === "all" ? null : organizationId;
      await exportEmailLogsCSV({
        q: query,
        status,
        contextType,
        from,
        to,
        organizationId: effectiveOrgId
      });
    } catch (err) {
      setError('Failed to export CSV');
      console.error('CSV export error:', err);
    }
  };

  const statusOptions = useMemo(() => STATUS_OPTIONS, []);
  const contextOptions = useMemo(() => EMAIL_CONTEXTS.map(c => ({ key: c.key, label: c.label })), []);

  // StandardListView columns
  const columns = useMemo(() => ([
    {
      label: 'Time',
      field: 'created_at',
      sortable: true,
      render: (r) => new Date(r.created_at).toLocaleString()
    },
    { 
      label: 'Organization', 
      field: 'organization_name', 
      sortable: true,
      render: (r) => r.organization_name || '-'
    },
    { label: 'Recipient', field: 'to_name', sortable: true },
    { label: 'Email', field: 'to_email', sortable: true },
    { label: 'Template', field: 'template_name', sortable: true },
    { 
      label: 'Context', 
      field: 'context_type', 
      sortable: true,
      render: (r) => r.context_type
    },
    {
      label: 'Status',
      field: 'status',
      sortable: true,
      render: (r) => <EmailStatusChip status={r.status} />
    },
    { label: 'Message ID', field: 'provider_message_id', sortable: false }
  ]), []);

  const onRowClick = (row) => {
    setSelected(row);
    setIsModalOpen(true);
  };

  if (!hasSuperOrTechAdminOnly()) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Not Authorized</Typography>
        <Typography variant="body2" sx={{ color: '#6b7280' }}>
          You do not have permission to view Email Notifications. Please contact your administrator.
        </Typography>
      </Box>
    );
  }

  // Quick filters row rendered by StandardListView beneath the title
  const QuickFilters = (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
      <FormControl size="small" sx={{ minWidth: 200 }}>
        <InputLabel>Organization</InputLabel>
        <Select
          label="Organization"
          value={selectedOrg}
          onChange={(e) => {
            const newValue = e.target.value;
            console.log('[EmailNotifications] Organization filter changed:', {
              oldValue: selectedOrg,
              newValue,
              organizationId
            });
            setSelectedOrg(newValue);
          }}
        >
          <MenuItem value="current">Current Organization</MenuItem>
          <MenuItem value="all">All Organizations</MenuItem>
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel>Status</InputLabel>
        <Select
          multiple
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          renderValue={(selected) => selected.length === 0 ? '' : `${selected.length} selected`}
        >
          {statusOptions.map(s => (
            <MenuItem key={s.value} value={s.value}>
              <Checkbox checked={status.indexOf(s.value) > -1} />
              <ListItemText primary={s.label} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 200 }}>
        <InputLabel>Context</InputLabel>
        <Select
          multiple
          label="Context"
          value={contextType}
          onChange={(e) => {
            const newValue = e.target.value;
            console.log('[EmailNotifications] Context filter changed:', {
              oldValue: contextType,
              newValue
            });
            setContextType(newValue);
          }}
          renderValue={(selected) => selected.length === 0 ? '' : `${selected.length} selected`}
        >
          {contextOptions.map(c => (
            <MenuItem key={c.key} value={c.key}>
              <Checkbox checked={contextType.indexOf(c.key) > -1} />
              <ListItemText primary={c.label} secondary={c.key} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        label="From"
        type="date"
        size="small"
        value={from}
        onChange={e => setFrom(e.target.value)}
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        label="To"
        type="date"
        size="small"
        value={to}
        onChange={e => setTo(e.target.value)}
        InputLabelProps={{ shrink: true }}
      />
      <Button 
        variant="contained" 
        onClick={() => {
          console.log('[EmailNotifications] Apply button clicked:', {
            selectedOrg,
            status,
            contextType,
            from,
            to,
            query,
            organizationId
          });
          setPage(0);
          applyUrlAndReload({ 
            page: 0,
            selectedOrg: selectedOrg,
            status: status,
            contextType: contextType,
            from: from,
            to: to,
            q: query
          });
        }} 
        disabled={loading}
      >
        Apply
      </Button>
      <Button 
        variant="outlined" 
        onClick={() => {
          console.log('[EmailNotifications] Clear All button clicked');
          setSelectedOrg("current");
          setStatus([]);
          setContextType([]);
          setFrom("");
          setTo("");
          setQuery("");
          setPage(0);
          applyUrlAndReload({ 
            page: 0,
            selectedOrg: "current",
            status: [],
            contextType: [],
            from: "",
            to: "",
            q: ""
          });
        }} 
        disabled={loading}
      >
        Clear All
      </Button>
    </Stack>
  );

  return (
    <Box sx={pageShellSx}>
      <Box sx={{ px: 2, pt: 2, pb: 0, flexShrink: 0 }}>
        <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: '8px 8px 0 0' }}>
          <Tabs
            value={activeNotificationsTab}
            onChange={(_, newValue) => setActiveNotificationsTab(newValue)}
            aria-label="Email notifications tabs"
          >
            <Tab value={NOTIFICATIONS_TABS.EMAIL_LOG} label="Email Log" />
            <Tab value={NOTIFICATIONS_TABS.QUEUED_FORECAST} label="Queued Email Forecast" />
          </Tabs>
        </Paper>
      </Box>

      {activeNotificationsTab === NOTIFICATIONS_TABS.QUEUED_FORECAST && (
        <Box sx={{ ...listFlexSx, px: 2, pt: 2 }}>
          <QueuedEmailForecast />
        </Box>
      )}

      {activeNotificationsTab === NOTIFICATIONS_TABS.EMAIL_LOG && (
      <>
      <Box sx={{ pl: 2, pr: 2, pt: 2, pb: 0, mb: 0.5, flexShrink: 0 }}>
        <StandardHighlight
          onExpandChange={(isExpanded) => setHighlightsExpanded(isExpanded)}
          customContent={
          <Box sx={{ width: '100%' }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1E2737', mb: 2 }}>
              Email Context Types and Sources
            </Typography>
            <Box sx={{ 
              display: 'flex',
              fontSize: 12, 
              color: '#6B7280', 
              fontWeight: 600, 
              mb: 1,
              pb: 1,
              borderBottom: '2px solid #E5E7EB'
            }}>
              <Box sx={{ flex: '0 0 33.33%', minWidth: '300px' }}>Context</Box>
              <Box sx={{ flex: '0 0 25%', minWidth: '150px' }}>Source</Box>
              <Box sx={{ flex: '1 1 auto', minWidth: '200px' }}>Trigger</Box>
            </Box>
            {EMAIL_CONTEXTS.map((c, index) => (
              <Box 
                key={c.key} 
                sx={{ 
                  display: 'flex',
                  fontSize: 12, 
                  color: '#374151', 
                  py: 1, 
                  borderTop: index > 0 ? '1px solid #F3F4F6' : 'none',
                  '&:hover': {
                    backgroundColor: '#F9FAFB'
                  }
                }}
              >
                <Box sx={{ flex: '0 0 33.33%', minWidth: '300px' }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 500 }}>{c.key}</Typography>
                </Box>
                <Box sx={{ flex: '0 0 25%', minWidth: '150px' }}>
                  <Typography sx={{ fontSize: 12, color: '#6B7280' }}>{c.source}</Typography>
                </Box>
                <Box sx={{ flex: '1 1 auto', minWidth: '200px' }}>
                  <Typography sx={{ fontSize: 12, color: '#6B7280' }}>{c.trigger}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        }
        />
      </Box>

      <Box sx={{ ...listFlexSx, px: 2, pt: 0.5 }}>
        <StandardListView
          {...listViewProps}
          title={"Email Notifications"}
          columns={columns}
          rows={rows}
          rightHeaderExtras={
          <Stack direction="row" spacing={1}>
            <IconButton onClick={() => { setPage(0); fetchData(); }} disabled={loading} title="Refresh">
              <RefreshIcon />
            </IconButton>
            <Button variant="outlined" startIcon={<SettingsIcon />} onClick={() => setSettingsOpen(true)}>
              Settings
            </Button>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={onExportCsv}>
              Export CSV
            </Button>
          </Stack>
        }
        // Quick filters row
        quickFilterComponent={QuickFilters}
        enableQuickFilter
        // Search (standard toolbar)
        onSearch={handleSearch}
        onClearSearch={handleClearSearch}
        searchQuery={query}
        // Pagination
        totalRows={total}
        rowsPerPage={pageSize}
        page={page}
        onChangePage={(e, newPage) => {
          setPage(newPage);
          applyUrlAndReload({ page: newPage });
        }}
        onChangeRowsPerPage={(e) => {
          const nextSize = parseInt(e.target.value, 10);
          setPageSize(nextSize);
          setPage(0);
          applyUrlAndReload({ pageSize: nextSize, page: 0 });
        }}
        // Row click
        onRowClick={onRowClick}
        readOnly
        showMoreIcon={false}
      />
      </Box>

      {/* Modals outside the main flex layout */}
      <StandardFormView
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={"Overview Email Notification"}
        subTitle={"Email Details"}
        flag={"view"}
        isPage={"email notifications"}
        editItems={selected || {}}
      />
      
      <EmailNotificationsSettings
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        organizationId={organizationId}
      />
      </>
      )}
    </Box>
  );
};

const Detail = ({ label, value }) => (
  <Box>
    <Typography variant="caption" sx={{ color: '#6b7280' }}>{label}</Typography>
    <Box sx={{ mt: 0.5 }}>
      {typeof value === 'string' || typeof value === 'number' ? (
        <Typography variant="body2">{String(value)}</Typography>
      ) : (
        value
      )}
    </Box>
  </Box>
);

export default EmailNotificationsPage;


